import { ReactNode } from 'react';
import { Button } from 'app/agriculture/components/Button/Button';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useGenericEntityFromId } from 'app/agriculture/queries/entities/useGenericEntityFromId';
import { Entity } from 'app/agriculture/queries/entities/types/Entity';
import { isEntityType } from 'app/agriculture/models/Entities';
import './LinkedEntityList.scss';
import { ContentLayout } from 'app/agriculture/components/ContentLayout/ContentLayout';

interface LinkedEntityListProps {
    entityIds: (string | number)[];
    icon?: ReactNode;
}

/**
 * Takes a list of entity (or asset) ids and returns a list of buttons that link to their respective detail pages.
 *
 * @param entityIds an array of entity ids
 * @param icon an icon to associate with each linked entity button
 */
export function LinkedEntityList({ entityIds, icon }: LinkedEntityListProps) {
    const getEntity = useGenericEntityFromId();
    const routeActions = useRouteActions();

    const entities = entityIds
        .map(getEntity)
        .filter((entity: Entity | undefined): entity is Entity => entity?.name !== undefined);

    if (entities.length === 0) return null;

    const buttons = entities.map<ReactNode>(({ id, type, name = '' }) => (
        <Button
            startIcon={icon}
            key={id}
            text={name}
            variant="compact"
            onClick={() => {
                if (isEntityType(type)) routeActions.goto(id, { type });
            }}
        />
    ));

    return (
        <ContentLayout gap={0} className="LinkedEntityList">
            {buttons}
        </ContentLayout>
    );
}
